import type { TFunction } from 'i18next';

import { MetaGamesEventType } from '../../dto/metagames.types';
import { MissionTargetTypeEnum, MissionTypeEnum, type MissionResponse } from '../../stores/missions/types';

function metagameCopyKeys(metagameKey: string | null | undefined) {
  switch (metagameKey) {
    case MetaGamesEventType.HAMMERS:
      return {
        name: 'missions.metagames.hammers.name',
        title: 'missions.metagames.hammers.title',
        unit: 'missions.progressUnit.boards.hammers',
      } as const;
    case MetaGamesEventType.PUZZLE:
      return {
        name: 'missions.metagames.puzzle.name',
        title: 'missions.metagames.puzzle.title',
        unit: 'missions.progressUnit.boards.puzzle',
      } as const;
    case MetaGamesEventType.TRAIL:
      return {
        name: 'missions.metagames.trail.name',
        title: 'missions.metagames.trail.title',
        unit: 'missions.progressUnit.boards.trail',
      } as const;
    default:
      return null;
  }
}

export function getMetagameLabel(metagameKey: string | null | undefined, t: TFunction): string {
  const keys = metagameCopyKeys(metagameKey);
  return keys ? t(keys.name) : '';
}

export function getMetagameProgressUnit(metagameKey: string | null | undefined, t: TFunction): string {
  const keys = metagameCopyKeys(metagameKey);
  return keys ? t(keys.unit) : '';
}

export function getBoardMissionTitle(metagameKey: string | null | undefined, goal: number, t: TFunction): string {
  const keys = metagameCopyKeys(metagameKey);
  const options = { count: goal, goal: goal.toLocaleString() };
  return keys ? t(keys.title, options) : t('missions.types.completeXBoards', options);
}

export function pickLocalizedCategoryName(categoryName: { EN: string; DE: string }, language: string): string {
  const lang = language.split('-')[0].toUpperCase();
  const localized = lang === 'DE' ? categoryName.DE : categoryName.EN;
  return localized || categoryName.EN;
}

// Builds the name-line under the mission title, per effective targetType. Empty string means
// "render no name" — used for ANY and for BE lookup misses (null target / empty name).
export function getMissionTargetName(
  mission: Pick<MissionResponse, 'missionType' | 'targetType' | 'game' | 'provider' | 'category' | 'metagame'>,
  t: TFunction,
  language: string,
): string {
  const { missionType, targetType, game, provider, category, metagame } = mission;
  // Race missions are always targetType ANY, so their name line comes from the type, not the target.
  if (missionType === MissionTypeEnum.GET_X_POINTS_RACE) return t('missions.types.raceTargetName');
  switch (targetType) {
    case MissionTargetTypeEnum.SLOT:
      return game.gameName;
    case MissionTargetTypeEnum.PROVIDER:
      return provider && provider.providerName !== ''
        ? `${provider.providerName} ${t('missions.types.providerSuffix')}`
        : '';
    case MissionTargetTypeEnum.CATEGORY: {
      if (!category) return '';
      const name = pickLocalizedCategoryName(category.categoryName, language);
      return name !== '' ? `${name} ${t('missions.types.categorySuffix')}` : '';
    }
    case MissionTargetTypeEnum.METAGAME:
      return getMetagameLabel(metagame?.metagameKey, t);
    default:
      return '';
  }
}
